import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scissors, X, Menu } from 'lucide-react';

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Services', href: '#services' },
  { label: 'About', href: '#about' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Testimonials', href: '#testimonials' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (href) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-cream/95 backdrop-blur-md shadow-md shadow-roseGold/10'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <button
              onClick={() => scrollTo('#home')}
              className="flex items-center gap-2 group"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-roseGold to-burgundy flex items-center justify-center shadow-md">
                <Scissors className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <span
                className="font-playfair font-bold text-xl md:text-2xl tracking-wide"
                style={{ color: scrolled ? '#1A1A1A' : '#FDF8F5' }}
              >
                Lamisking
              </span>
            </button>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollTo(link.href)}
                  className={`font-inter text-sm font-medium tracking-wide transition-colors duration-300 hover:text-roseGold relative group ${
                    scrolled ? 'text-charcoal' : 'text-white/90'
                  }`}
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-roseGold transition-all duration-300 group-hover:w-full rounded-full" />
                </button>
              ))}

              <button
                onClick={() => scrollTo('#booking')}
                className="ml-4 px-5 py-2.5 rounded-full bg-gradient-to-r from-roseGold to-roseGoldDark text-white font-inter font-semibold text-sm tracking-wide shadow-lg shadow-roseGold/30 hover:shadow-roseGold/50 hover:scale-105 transition-all duration-300"
              >
                Book Now
              </button>
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 rounded-lg"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu
                className={`w-6 h-6 ${scrolled ? 'text-charcoal' : 'text-white'}`}
                strokeWidth={1.5}
              />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
              onClick={() => setMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 260 }}
              className="fixed top-0 right-0 h-full w-72 z-50 flex flex-col"
              style={{ background: 'linear-gradient(160deg, #1A1A1A 0%, #2d1220 100%)' }}
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-roseGold to-burgundy flex items-center justify-center">
                    <Scissors className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
                  </div>
                  <span className="font-playfair text-xl text-champagne">Lamisking</span>
                </div>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="p-1 text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Drawer links */}
              <nav className="flex flex-col gap-1 p-6 flex-1">
                {navLinks.map((link, i) => (
                  <motion.button
                    key={link.href}
                    initial={{ x: 40, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.07 + 0.1 }}
                    onClick={() => scrollTo(link.href)}
                    className="text-left px-4 py-3.5 rounded-xl font-inter text-white/80 hover:text-white hover:bg-white/10 text-base font-medium tracking-wide transition-all duration-200"
                  >
                    {link.label}
                  </motion.button>
                ))}
              </nav>

              <div className="p-6 border-t border-white/10">
                <button
                  onClick={() => scrollTo('#booking')}
                  className="w-full py-3.5 rounded-full bg-gradient-to-r from-roseGold to-roseGoldDark text-white font-inter font-semibold text-sm tracking-wide shadow-lg shadow-roseGold/30 hover:shadow-roseGold/50 transition-all duration-300"
                >
                  Book Now
                </button>
                <p className="text-center text-white/40 text-xs mt-4 font-inter">
                  CVS Plaza, Wuse 2, Abuja
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
